import React, { useState, useEffect } from 'react';
import { Card, Table, Input, Button, Row, Col, Typography, message, Modal, InputNumber, Statistic, Steps, Divider, Tabs, Form, Select, Tag } from 'antd';
import { ShoppingCartOutlined, SearchOutlined, CheckCircleOutlined, WalletOutlined, SwapOutlined, DollarOutlined } from '@ant-design/icons';
import axios from 'axios';

import { formatCurrency } from '../utils/currency';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Buyback = () => {
    const [activeTab, setActiveTab] = useState('cash');
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Trade-in State
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [tradeInInvoiceId, setTradeInInvoiceId] = useState(null);
    const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    // Fetch Products for Trade-in Cart
    const fetchProducts = async (search = '') => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/products?search=${search}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProducts(res.data);
        } catch (error) {
            // message.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'tradein') fetchProducts();
    }, [activeTab]);

    // Cart Logic (Duplicated from POS for V1 speed)
    const addToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
            return [...prev, { ...product, qty: 1 }];
        });
    };
    const updateQty = (id, qty) => setCart(prev => qty <= 0 ? prev.filter(item => item.id !== id) : prev.map(item => item.id === id ? { ...item, qty } : item));
    const totalTTC = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);

    const checkSerialNumber = async (serial) => {
        if (!serial) return;
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/buyback/check-serial/${serial}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.exists) {
                message.error('Serial number already exists in system!');
                return true;
            }
            return false;
        } catch (e) {
            return false;
        }
    };

    // Handlers
    const onFinishCash = async (values) => {
        const isDuplicate = await checkSerialNumber(values.serial_number);
        if (isDuplicate) return;

        setPendingValues(values);
        setConfirmModalVisible(true);
    };

    const onFinishTradeIn = async (values) => {
        if (cart.length === 0) return message.warning('Cart is empty for trade-in');

        const isDuplicate = await checkSerialNumber(values.serial_number);
        if (isDuplicate) return;

        // Calculate Net
        const tradeValue = parseFloat(values.buy_price);
        const netToPay = totalTTC - tradeValue;

        if (netToPay < 0) return message.error('Trade-in value cannot exceed new purchase amount (V1 restriction)');

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            // 1. Create Trade-in + Invoice
            const res = await axios.post('/api/buyback/tradein', {
                item_details: values,
                cart_items: cart,
                socid: 1 // Default customer
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.status === 'success') {
                setTradeInInvoiceId(res.data.invoice_id);
                setCheckoutModalVisible(true);
                setCurrentStep(1);
                message.success('Trade-in Draft Created');
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.error || 'Trade-in failed');
        } finally {
            setLoading(false);
        }
    };

    const confirmCashBuyback = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/buyback/cash', pendingValues, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Modal.success({
                title: 'Cash Buyback Successful',
                content: (
                    <div>
                        <p>Asset ID: <strong>{res.data.asset_id}</strong></p>
                        <p>Serial Number: <strong>{res.data.serial_number}</strong></p>
                        <p>Dolibarr Product & Paid Invoice Created.</p>
                        <p style={{ fontSize: '1.2em', marginTop: 10 }}>Please pay customer: <strong>{formatCurrency(pendingValues.buy_price)}</strong></p>
                    </div>
                ),
            });
            form.resetFields();
            setConfirmModalVisible(false);
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.error || 'Buyback failed');
        } finally {
            setLoading(false);
        }
    };

    // Shared Form
    const BuybackForm = ({ onFinish, submitText, icon }) => (
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ category: 'Smartphones' }}>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                        <Select options={[
                            { label: 'Smartphones', value: 'Smartphones' },
                            { label: 'Tablets', value: 'Tablets' },
                            { label: 'Laptops', value: 'Laptops' },
                            { label: 'Gaming Consoles', value: 'Gaming Consoles' },
                            { label: 'Accessories', value: 'Accessories' },
                        ]} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="brand" label="Brand" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Apple, Sony" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="model" label="Model" rules={[{ required: true }]}>
                        <Input placeholder="e.g. iPhone 15 Pro, PS5" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="storage" label="Storage / Version" rules={[{ required: true }]}>
                        <Input placeholder="e.g. 256GB, Disc Edition" />
                    </Form.Item>
                </Col>
                <Col span={16}>
                    <Form.Item name="serial_number" label="Serial Number (Unique)" rules={[{ required: true }]}>
                        <Input placeholder="IMEI or Serial Number" onBlur={(e) => checkSerialNumber(e.target.value)} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="condition" label="Condition" rules={[{ required: true }]}>
                        <Select>
                            <Option value="A">Grade A (Like New)</Option>
                            <Option value="B">Grade B (Good)</Option>
                            <Option value="C">Grade C (Fair)</Option>
                            <Option value="D">Grade D (Broken)</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={16}>
                    <Form.Item name="accessories" label="Accessories Included">
                        <Select mode="multiple" placeholder="Select included items">
                            <Option value="Box">Original Box</Option>
                            <Option value="Cable">Charging Cable</Option>
                            <Option value="Adapter">Power Adapter</Option>
                            <Option value="Controller">Controller</Option>
                            <Option value="Headphones">Headphones</Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="buy_price" label="Buy Price (JOD)" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="notes" label="Additional Notes">
                        <Input placeholder="Any scratches, defects, etc." />
                    </Form.Item>
                </Col>
            </Row>

            <Button type="primary" htmlType="submit" icon={icon} loading={loading} block size="large" style={{ marginTop: 10 }}>
                {submitText}
            </Button>
        </Form>
    );

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Buyback Center</Title>
            <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
                <Tabs.TabPane tab={<span><DollarOutlined /> Cash Buyback</span>} key="cash">
                    <Card style={{ maxWidth: 800, margin: '0 auto' }}>
                        <BuybackForm onFinish={onFinishCash} submitText="Process Cash Buyback" icon={<DollarOutlined />} />
                    </Card>
                </Tabs.TabPane>

                <Tabs.TabPane tab={<span><SwapOutlined /> Trade-In</span>} key="tradein">
                    <Row gutter={24}>
                        <Col span={10}>
                            <Card title="1. Used Item Details">
                                <BuybackForm onFinish={onFinishTradeIn} submitText="Create Trade-in Deal" icon={<SwapOutlined />} />
                            </Card>
                        </Col>
                        <Col span={14}>
                            <Card title="2. Select New Items (Cart)">
                                <Search placeholder="Search new products..." onSearch={fetchProducts} style={{ marginBottom: 16 }} />
                                <Table
                                    dataSource={products}
                                    columns={[
                                        { title: 'Item', dataIndex: 'label', key: 'label' },
                                        { title: 'Price', dataIndex: 'price', render: v => formatCurrency(v) },
                                        { render: (_, r) => <Button size="small" onClick={() => addToCart(r)}>Add</Button> }
                                    ]}
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    rowKey="id"
                                />
                                <Divider />
                                <Title level={4}>Cart Total: {formatCurrency(totalTTC)}</Title>
                                {cart.map(item => (
                                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text>{item.label} x {item.qty}</Text>
                                        <Text>{formatCurrency(item.price * item.qty)}</Text>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    </Row>
                </Tabs.TabPane>
            </Tabs>

            {/* Confirmation Modal */}
            <Modal
                title="Confirm Cash Buyback"
                open={confirmModalVisible}
                onOk={confirmCashBuyback}
                onCancel={() => setConfirmModalVisible(false)}
                confirmLoading={loading}
                okText="Process Buyback"
                width={500}
            >
                {pendingValues && (
                    <div style={{ padding: '10px 0' }}>
                        <p>Are you sure you want to process this buyback?</p>
                        <Divider />
                        <Row gutter={16}>
                            <Col span={12}><Text type="secondary">Product:</Text></Col>
                            <Col span={12}><Text strong>{pendingValues.brand} {pendingValues.model} {pendingValues.storage}</Text></Col>
                        </Row>
                        <Row gutter={16} style={{ marginTop: 8 }}>
                            <Col span={12}><Text type="secondary">Serial Number:</Text></Col>
                            <Col span={12}><Text strong>{pendingValues.serial_number}</Text></Col>
                        </Row>
                        <Row gutter={16} style={{ marginTop: 8 }}>
                            <Col span={12}><Text type="secondary">Condition:</Text></Col>
                            <Col span={12}><Tag color="blue">Grade {pendingValues.condition}</Tag></Col>
                        </Row>
                        <Divider />
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">Buy Price:</Text>
                            <Title level={3} style={{ marginTop: 5 }}>{formatCurrency(pendingValues.buy_price)}</Title>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Checkout Modal for Tradein */}
            <Modal
                title="Trade-in Checkout"
                open={checkoutModalVisible}
                footer={null}
                closable={false}
                width={600}
            >
                <Steps current={currentStep} items={[
                    { title: 'Draft', icon: <ShoppingCartOutlined /> },
                    { title: 'Validate', icon: <CheckCircleOutlined /> },
                    { title: 'Pay Balance', icon: <WalletOutlined /> },
                ]} />
                <Divider />
                <div style={{ textAlign: 'center' }}>
                    {currentStep === 1 && (
                        <div>
                            <p>Trade-in Invoice Created. Please validate.</p>
                            <Button type="primary" onClick={validateInvoice} loading={loading}>Validate Invoice</Button>
                        </div>
                    )}
                    {currentStep === 2 && (
                        <div>
                            <p>Total New Items: <strong>{formatCurrency(totalTTC)}</strong></p>
                            <p>Trade-in Credit: <Text type="success">- {formatCurrency(form.getFieldValue('buy_price'))}</Text></p>
                            <Divider />
                            <Title level={4}>Net To Pay: {formatCurrency(totalTTC - parseFloat(form.getFieldValue('buy_price') || 0))}</Title>
                            <Button type="primary" size="large" onClick={processPayment} loading={loading}>Collect Balance</Button>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default Buyback;
